import { supabase } from "~/integrations/supabase/client";
import type {
  IncomeStatementFilters,
  IncomeStatementLine,
  IncomeStatementGroupedData,
  IncomeStatementClase,
  IncomeStatementSubClase,
  IncomeStatementSubClase2,
  IncomeStatementGrupo,
  IncomeStatementAccount,
} from "~/types/contabilidad";

export const incomeStatementService = {
  /**
   * Fetch income statement data from the database (3 columns)
   */
  async getIncomeStatement(
    filters: IncomeStatementFilters
  ): Promise<IncomeStatementLine[]> {
    const { data, error } = await supabase.rpc(
      "get_income_statement_cumulative",
      {
        p_year: filters.year,
        p_month: filters.month,
        p_company_id: filters.companyId,
        p_template_id: filters.templateId,
      }
    );

    if (error) throw error;
    return (data || []) as IncomeStatementLine[];
  },

  /**
   * Transform flat data into hierarchical structure with 3 columns
   */
  groupIncomeStatementData(
    lines: IncomeStatementLine[]
  ): IncomeStatementGroupedData {
    // Group by Clase
    const claseMap = new Map<string, IncomeStatementLine[]>();

    lines.forEach((line) => {
      const existing = claseMap.get(line.clase) || [];
      existing.push(line);
      claseMap.set(line.clase, existing);
    });

    // Build hierarchical structure
    const clases: IncomeStatementClase[] = [];

    claseMap.forEach((claseLines, claseName) => {
      const subClaseMap = new Map<string, IncomeStatementLine[]>();

      // Group by SubClase
      claseLines.forEach((line) => {
        const key = line.sub_clase || "Sin SubClase";
        const existing = subClaseMap.get(key) || [];
        existing.push(line);
        subClaseMap.set(key, existing);
      });

      const subClases: IncomeStatementSubClase[] = [];

      subClaseMap.forEach((subClaseLines, subClaseName) => {
        const subClase2Map = new Map<string, IncomeStatementLine[]>();

        // Group by SubClase2
        subClaseLines.forEach((line) => {
          const key = line.sub_clase_2 || "Sin SubClase 2";
          const existing = subClase2Map.get(key) || [];
          existing.push(line);
          subClase2Map.set(key, existing);
        });

        const subClases2: IncomeStatementSubClase2[] = [];

        subClase2Map.forEach((subClase2Lines, subClase2Name) => {
          const grupoMap = new Map<string, IncomeStatementLine[]>();

          // Group by Grupo
          subClase2Lines.forEach((line) => {
            const key = line.grupo_cuenta_id || "Sin Grupo";
            const existing = grupoMap.get(key) || [];
            existing.push(line);
            grupoMap.set(key, existing);
          });

          const grupos: IncomeStatementGrupo[] = [];

          grupoMap.forEach((grupoLines) => {
            const accounts: IncomeStatementAccount[] = grupoLines.map(
              (line) => ({
                account_id: line.account_id,
                account_code: line.account_code,
                account_name: line.account_name,
                anterior: line.saldo_acumulado_anterior,
                actual: line.saldo_mes_actual,
                total: line.saldo_acumulado_total,
              })
            );

            const grupoLine = grupoLines[0];
            grupos.push({
              grupo_cuenta_id: grupoLine.grupo_cuenta_id,
              grupo_cuenta_code: grupoLine.grupo_cuenta_code,
              grupo_cuenta_name: grupoLine.grupo_cuenta_name,
              orden_grupo_cuenta: grupoLine.orden_grupo_cuenta || 0,
              anterior: accounts.reduce((sum, acc) => sum + acc.anterior, 0),
              actual: accounts.reduce((sum, acc) => sum + acc.actual, 0),
              total: accounts.reduce((sum, acc) => sum + acc.total, 0),
              accounts,
            });
          });

          // Sort grupos by order
          grupos.sort((a, b) => a.orden_grupo_cuenta - b.orden_grupo_cuenta);

          const subClase2Line = subClase2Lines[0];
          subClases2.push({
            sub_clase_2: subClase2Name,
            orden_subclase_2: subClase2Line.orden_subclase_2 || 0,
            anterior: grupos.reduce((sum, g) => sum + g.anterior, 0),
            actual: grupos.reduce((sum, g) => sum + g.actual, 0),
            total: grupos.reduce((sum, g) => sum + g.total, 0),
            grupos,
          });
        });

        // Sort subClases2 by order
        subClases2.sort((a, b) => a.orden_subclase_2 - b.orden_subclase_2);

        const subClaseLine = subClaseLines[0];
        subClases.push({
          sub_clase: subClaseName,
          orden_subclase: subClaseLine.orden_subclase || 0,
          anterior: subClases2.reduce((sum, sc2) => sum + sc2.anterior, 0),
          actual: subClases2.reduce((sum, sc2) => sum + sc2.actual, 0),
          total: subClases2.reduce((sum, sc2) => sum + sc2.total, 0),
          subClases2,
        });
      });

      // Sort subClases by order
      subClases.sort((a, b) => a.orden_subclase - b.orden_subclase);

      const claseLine = claseLines[0];
      clases.push({
        clase: claseName,
        orden_clase: claseLine.orden_clase || 0,
        anterior: subClases.reduce((sum, sc) => sum + sc.anterior, 0),
        actual: subClases.reduce((sum, sc) => sum + sc.actual, 0),
        total: subClases.reduce((sum, sc) => sum + sc.total, 0),
        subClases,
      });
    });

    // Sort clases by order
    clases.sort((a, b) => a.orden_clase - b.orden_clase);

    // Calculate totals by clase type
    const ingresos = clases.find((c) => c.clase === "Ingresos") || {
      anterior: 0,
      actual: 0,
      total: 0,
    };
    const costos = clases.find((c) => c.clase === "Costos") || {
      anterior: 0,
      actual: 0,
      total: 0,
    };
    const gastos = clases.find((c) => c.clase === "Gastos") || {
      anterior: 0,
      actual: 0,
      total: 0,
    };

    // Calculate derived totals
    const utilidadBruta = {
      anterior: ingresos.anterior + costos.anterior, // costos is negative
      actual: ingresos.actual + costos.actual,
      total: ingresos.total + costos.total,
    };

    const utilidadOperacional = {
      anterior: utilidadBruta.anterior + gastos.anterior, // gastos is negative
      actual: utilidadBruta.actual + gastos.actual,
      total: utilidadBruta.total + gastos.total,
    };

    const margenOperacional =
      ingresos.total !== 0
        ? (utilidadOperacional.total / ingresos.total) * 100
        : 0;

    return {
      clases,
      totals: {
        ingresos: {
          anterior: ingresos.anterior,
          actual: ingresos.actual,
          total: ingresos.total,
        },
        costos: {
          anterior: costos.anterior,
          actual: costos.actual,
          total: costos.total,
        },
        gastos: {
          anterior: gastos.anterior,
          actual: gastos.actual,
          total: gastos.total,
        },
        utilidadBruta,
        utilidadOperacional,
        margenOperacional,
      },
    };
  },
};
